import {
    AccordionItem,
    ChipAccordionPanel,
    NestedAccordion as DefaultNestedAccordion,
} from 'components/Accordion';
import {
    FilterAccordionButton as DefaultFilterAccordionButton,
    FilterAccordionNestedButton,
    FilterAccordionNestedItem,
    FilterCheckbox,
    FilterChipItemButton,
} from 'components/FilterAccordion';
import {
    findCatThree,
    handleFlavorFilterChange,
} from 'components/FlavorFilters/utils';
import { BiLeaf } from 'react-icons/bi';
import { FiChevronRight } from 'react-icons/fi';
import styled from 'styled-components';
import { useCatThreeFlavorCounts } from 'utils/hooks/useFlavorsCounts';
import { useFetchFlavors } from 'utils/hooks/api';
import { useUID } from 'react-uid';

export function FlavorFilterOption({ children, value, ...props }) {
    return (
        <FilterCheckbox
            css={({ theme, checked }) =>
                `background-color: ${checked ? theme.purple_5 : theme.gray_6};`
            }
            {...props}
        >
            {children}
        </FilterCheckbox>
    );
}

function FlavorFilterChipItemButton({ children, value, ...props }) {
    return <FilterChipItemButton {...props}>{children}</FilterChipItemButton>;
}

const TruncatedSpan = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: inline-block;
`;

function renderRootFilterChipLabel({ options, flavorFilter }) {
    const { label, depth, catTwo } = flavorFilter;
    if (depth === 3) {
        return (
            <>
                {catTwo} <FiChevronRight /> {label}
            </>
        );
    } else if (depth === 4) {
        const catThreeLabel = findCatThree(flavorFilter, options)?.label;
        return (
            <>
                <TruncatedSpan>{catTwo}</TruncatedSpan> <FiChevronRight />{' '}
                <TruncatedSpan>{catThreeLabel}</TruncatedSpan>{' '}
                <FiChevronRight /> <TruncatedSpan>{label}</TruncatedSpan>
            </>
        );
    }
    return null;
}

function FlavorFilterAccordionItem({
    isOpen,
    toggleOpen,
    active,
    panelId,
    options,
    data,
    isLoadingData,
    filterMissingData,
    label = 'Flavors',
    mixpanelContext = 'FilterAccordionNestedItem',
    Icon = BiLeaf,
    FilterOption = FlavorFilterOption,
    FilterChipItemButton = FlavorFilterChipItemButton,
    FilterAccordionButton = DefaultFilterAccordionButton,
    NestedAccordion = DefaultNestedAccordion,
    ...props
}) {
    const stableId = useUID();
    const hasActiveFilters =
        !!props.filters.flavors && props.filters.flavors.length > 0;
    const { catThreeFlavorCounts } = useCatThreeFlavorCounts({
        data,
        isLoading: isLoadingData,
        filterMissingData,
    });
    const flavorsQuery = useFetchFlavors();
    const catTwoLabels = flavorsQuery.data?.catTwoLabels || {};
    return (
        <>
            <AccordionItem $isOpen={isOpen}>
                <FilterAccordionButton
                    onClick={(e) => toggleOpen()}
                    isActive={isOpen}
                    isOpen={isOpen}
                    Icon={Icon}
                    {...(isOpen && {
                        'aria-controls': `${stableId}-${panelId}`,
                    })}
                >
                    {label}
                </FilterAccordionButton>
                {!isOpen && hasActiveFilters && (
                    <ChipAccordionPanel
                        $liMaxWidth={'100%'}
                        $bgColor={props.$FilterAccordionNestedItemBgColor}
                    >
                        {props.filters.flavors.map((flavorFilter) => (
                            <FilterChipItemButton
                                onClick={() =>
                                    handleFlavorFilterChange({
                                        option: flavorFilter,
                                        filters: props.filters,
                                        setFilter: props.setFilter,
                                        groupedFlavorsOptions: options,
                                        isChecked: true,
                                        isChip: true,
                                        onChange: props.getOnChange,
                                    })
                                }
                                {...flavorFilter}
                                $maxWidth={'100%'}
                                key={flavorFilter.value}
                            >
                                {renderRootFilterChipLabel({
                                    flavorFilter,
                                    options,
                                })}
                            </FilterChipItemButton>
                        ))}
                    </ChipAccordionPanel>
                )}
            </AccordionItem>
            <NestedAccordion $isActive={isOpen}>
                {isOpen &&
                    options &&
                    Object.keys(options).length &&
                    Object.keys(options)?.map((option) => {
                        return (
                            <FilterAccordionNestedItem
                                key={option}
                                label={catTwoLabels[option] || option}
                                options={options[option]}
                                mixpanelContext="FilterAccordionNestedItem"
                                isOpen={active?.includes(option)}
                                toggleOpen={() =>
                                    props.nestedToggleOpen(`flavors>${option}`)
                                }
                                appliedFilters={props.filters.flavors}
                                onChange={props.getOnChange}
                                mixpanelMeta={props.mixpanelMeta}
                                panelId="cards-filter-flavors"
                                FilterOption={FlavorFilterOption}
                                FilterChipItemButton={
                                    FlavorFilterChipItemButton
                                }
                                FilterAccordionButton={
                                    FilterAccordionNestedButton
                                }
                                filters={props.filters}
                                setFilter={props.setFilter}
                                catThreeFlavorCounts={catThreeFlavorCounts}
                                isLoadingData={isLoadingData}
                                $FilterAccordionNestedItemBgColor={
                                    props.$FilterAccordionNestedItemBgColor
                                }
                                groupedFlavorsOptions={options}
                            />
                        );
                    })}
            </NestedAccordion>
        </>
    );
}

export default FlavorFilterAccordionItem;
